import { useState, useEffect, useCallback, useMemo } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import Layout from '../../components/layout/Layout';
import SharedCalendars from './SharedCalendars';
import '../../components/layout/Layout.css';
import { getBookingWindow } from './bookingDates';
import './Booking.css';

const BASE_NAV = [
  { path: '/dashboard', label: 'Dashboard', icon: '▦', exact: true },
  { path: '/dashboard/sessions', label: 'My Sessions', icon: '📋' },
  { path: '/dashboard/book', label: 'Book a Session', icon: '➕' },
  { path: '/dashboard/players', label: 'My Players', icon: '⚾' },
  { path: '/dashboard/profile', label: 'My Profile', icon: '👤' },
  { path: '/dashboard/support', label: 'Support', icon: '🎫' },
];

const formatTime = t => { if (!t) return ''; const [h,m] = t.split(':'); const hour=parseInt(h); return `${hour>12?hour-12:hour||12}:${m} ${hour>=12?'PM':'AM'}`; };
const formatDate = d => { if (!d) return '—'; const parts = d.split('T')[0].split('-'); const date = new Date(parseInt(parts[0]), parseInt(parts[1])-1, parseInt(parts[2])); return date.toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' }); };
const formatDateFull = d => { if (!d) return '—'; const parts = d.split('T')[0].split('-'); const date = new Date(parseInt(parts[0]), parseInt(parts[1])-1, parseInt(parts[2])); return date.toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric', year:'numeric' }); };
const STATUS_COLORS = { confirmed:'badge-success', pending:'badge-warning', cancelled:'badge-error', completed:'badge-info', no_show:'badge-default' };

// ---- Instructor Avatar ----
function InstructorAvatar({ photo, first, last, size = 48 }) {
  return (
    <div style={{ width:`${size}px`, height:`${size}px`, borderRadius:'50%', background:'var(--bg-elevated)', border:'2px solid var(--border-light)', overflow:'hidden', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:'700', fontSize:`${size*0.3}px`, color:'var(--text-secondary)' }}>
      {photo ? <img src={photo} alt={first} style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : `${first?.[0]}${last?.[0]}`}
    </div>
  );
}

// ---- Cancel Modal ----
function CancelModal({ session, onClose, onCancelled }) {
  const [reason, setReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const handleCancel = async () => {
    setCancelling(true);
    try { await axios.patch(`/api/booking/sessions/${session.id}/cancel`, { reason }); onCancelled(); onClose(); }
    catch (err) { console.error(err); }
    finally { setCancelling(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header"><span className="modal-title">CANCEL SESSION</span><button className="modal-close" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <p style={{ color:'var(--text-secondary)', fontSize:'0.9rem', lineHeight:'1.6' }}>Are you sure you want to cancel your session with <strong style={{ color:'var(--text-primary)' }}>{session.instructor_first} {session.instructor_last}</strong> on {formatDateFull(session.session_date)} at {formatTime(session.start_time)}?</p>
          <div className="form-group">
            <label className="form-label">Reason for cancellation (optional)</label>
            <textarea className="form-input" rows={3} placeholder="e.g. Schedule conflict, change of plans, etc." value={reason} onChange={e => setReason(e.target.value)} style={{ resize:'vertical' }} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline btn-sm" onClick={onClose} disabled={cancelling}>Keep Session</button>
          <button className="btn btn-sm" style={{ background:'var(--error)', color:'#fff' }} onClick={handleCancel} disabled={cancelling}>{cancelling ? 'Cancelling...' : 'Yes, Cancel'}</button>
        </div>
      </div>
    </div>
  );
}

// ---- Session Detail Modal ----
function SessionDetailModal({ session, onClose, onCancelRequest }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:'520px' }}>
        <div className="modal-header"><span className="modal-title">SESSION DETAILS</span><button className="modal-close" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <div style={{ display:'flex', alignItems:'center', gap:'16px', padding:'12px', background:'var(--bg-elevated)', borderRadius:'var(--radius-md)', marginBottom:'4px' }}>
            <InstructorAvatar photo={session.instructor_photo} first={session.instructor_first} last={session.instructor_last} size={56} />
            <div>
              <div style={{ fontWeight:'600', color:'var(--text-primary)', fontSize:'1rem' }}>{session.instructor_first} {session.instructor_last}</div>
              <div style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>Instructor</div>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
            {[
              ['Date', formatDateFull(session.session_date)],
              ['Time', `${formatTime(session.start_time)} – ${formatTime(session.end_time)}`],
              ['Duration', `${session.duration_minutes} min`],
              ['Player', session.player_first ? `${session.player_first} ${session.player_last}` : '—'],
              ['Status', session.status],
              ['Type', session.session_type],
            ].map(([label, value]) => (
              <div key={label} style={{ background:'var(--bg-elevated)', borderRadius:'var(--radius-md)', padding:'10px 14px' }}>
                <div style={{ fontSize:'0.7rem', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.08em', fontWeight:'600', marginBottom:'4px' }}>{label}</div>
                <div style={{ fontSize:'0.875rem', color:'var(--text-primary)', fontWeight:'500', textTransform: label === 'Status' || label === 'Type' ? 'capitalize' : 'none' }}>{value}</div>
              </div>
            ))}
          </div>
          {session.notes && (
            <div style={{ background:'var(--bg-elevated)', borderRadius:'var(--radius-md)', padding:'12px 14px' }}>
              <div style={{ fontSize:'0.7rem', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.08em', fontWeight:'600', marginBottom:'6px' }}>Notes</div>
              <p style={{ fontSize:'0.875rem', color:'var(--text-secondary)', lineHeight:'1.5' }}>{session.notes}</p>
            </div>
          )}
          {session.cancellation_reason && (
            <div style={{ background:'rgba(248,113,113,0.08)', border:'1px solid rgba(248,113,113,0.2)', borderRadius:'var(--radius-md)', padding:'10px 14px' }}>
              <div style={{ fontSize:'0.7rem', color:'var(--error)', textTransform:'uppercase', letterSpacing:'0.08em', fontWeight:'600', marginBottom:'4px' }}>Cancellation Reason</div>
              <p style={{ fontSize:'0.875rem', color:'var(--text-secondary)' }}>{session.cancellation_reason}</p>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline btn-sm" onClick={onClose}>Close</button>
          {session.status !== 'cancelled' && session.status !== 'completed' && (
            <button className="btn btn-sm" style={{ background:'var(--error)', color:'#fff' }} onClick={() => { onClose(); onCancelRequest(session); }}>Cancel Session</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Parent Home Dashboard ----
function ParentHome({ supportBadge }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get('/api/parent/stats')
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="page">
      <div className="page-header"><h1 className="page-title">DASHBOARD</h1><p className="page-subtitle">Welcome back! Here's your training overview.</p></div>

      {supportBadge > 0 && (
        <div onClick={() => navigate('/dashboard/support')} style={{
          display:'flex', alignItems:'center', gap:'12px', padding:'12px 16px',
          background:'rgba(96,165,250,0.08)', border:'1px solid rgba(96,165,250,0.25)',
          borderRadius:'var(--radius-md)', marginBottom:'16px', cursor:'pointer', transition:'background 0.15s',
        }}
          onMouseEnter={e => e.currentTarget.style.background='rgba(96,165,250,0.14)'}
          onMouseLeave={e => e.currentTarget.style.background='rgba(96,165,250,0.08)'}>
          <span style={{ fontSize:'1.1rem' }}>🎫</span>
          <div style={{ flex:1 }}>
            <span style={{ fontWeight:'600', fontSize:'0.875rem', color:'#60a5fa' }}>
              {supportBadge} new support {supportBadge === 1 ? 'reply' : 'replies'}
            </span>
            <span style={{ fontSize:'0.8rem', color:'var(--text-muted)', marginLeft:'8px' }}>— click to view</span>
          </div>
          <span style={{ color:'var(--text-muted)', fontSize:'0.8rem' }}>→</span>
        </div>
      )}

      {loading ? <p style={{ color:'var(--text-muted)' }}>Loading...</p> : !data ? null : (
        <>
          {/* Pending alert */}
          {parseInt(data.stats.pending || 0) > 0 && (
            <div style={{ background:'rgba(250,204,21,0.08)', border:'1px solid rgba(250,204,21,0.3)', borderRadius:'var(--radius-lg)', padding:'16px 20px', marginBottom:'24px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'12px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                <span style={{ fontSize:'1.3rem' }}>⏳</span>
                <div>
                  <div style={{ fontWeight:'600', color:'#facc15', fontSize:'0.95rem' }}>{data.stats.pending} Session{parseInt(data.stats.pending)>1?'s':''} Awaiting Confirmation</div>
                  <div style={{ fontSize:'0.8rem', color:'var(--text-muted)', marginTop:'2px' }}>Your instructor will confirm shortly.</div>
                </div>
              </div>
              <button className="btn btn-sm" style={{ background:'#facc15', color:'#0a0a0a', fontWeight:'700' }} onClick={() => navigate('/dashboard/sessions')}>View Sessions</button>
            </div>
          )}

          {/* Stats */}
          <div className="stats-grid" style={{ marginBottom:'24px' }}>
            <div className="stat-card"><div className="stat-card-label">Upcoming</div><div className="stat-card-value">{data.stats.upcoming}</div></div>
            <div className="stat-card"><div className="stat-card-label">Confirmed</div><div className="stat-card-value" style={{ color:'#4ade80' }}>{data.stats.confirmed}</div></div>
            <div className="stat-card"><div className="stat-card-label">Completed</div><div className="stat-card-value">{data.stats.completed}</div></div>
            <div className="stat-card"><div className="stat-card-label">Cancelled</div><div className="stat-card-value" style={{ color:'#f87171' }}>{data.stats.cancelled}</div></div>
            <div className="stat-card"><div className="stat-card-label">Total Sessions</div><div className="stat-card-value">{data.stats.total}</div></div>
          </div>

          {/* Recent sessions */}
          {data.recentSessions.length > 0 && (
            <div className="table-container" style={{ marginBottom:'24px' }}>
              <div className="table-toolbar">
                <span className="table-toolbar-title">Recent Sessions</span>
                <button className="btn btn-outline btn-sm" onClick={() => navigate('/dashboard/sessions')}>View All</button>
              </div>
              <table>
                <thead><tr><th>Instructor</th><th>Date</th><th>Time</th><th>Player</th><th>Status</th></tr></thead>
                <tbody>
                  {data.recentSessions.map(s => (
                    <tr key={s.id}>
                      <td>
                        <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                          <InstructorAvatar photo={s.instructor_photo} first={s.instructor_first} last={s.instructor_last} size={32} />
                          <span className="td-primary">{s.instructor_first} {s.instructor_last}</span>
                        </div>
                      </td>
                      <td>{formatDate(s.session_date)}</td>
                      <td>{formatTime(s.start_time)}</td>
                      <td>{s.player_first ? `${s.player_first} ${s.player_last}` : '—'}</td>
                      <td><span className={`badge ${STATUS_COLORS[s.status]||'badge-default'}`}>{s.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Quick actions */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px,1fr))', gap:'16px' }}>
            <div className="card" style={{ cursor:'pointer', transition:'border-color var(--transition)' }} onClick={() => navigate('/dashboard/book')}
              onMouseEnter={e => e.currentTarget.style.borderColor='var(--border-light)'}
              onMouseLeave={e => e.currentTarget.style.borderColor='var(--border)'}>
              <div style={{ fontSize:'1.5rem', marginBottom:'12px' }}>➕</div>
              <div style={{ fontWeight:'600', color:'var(--text-primary)', marginBottom:'4px' }}>Book a Session</div>
              <div style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>Schedule training with an instructor</div>
            </div>
            <div className="card" style={{ cursor:'pointer', transition:'border-color var(--transition)' }} onClick={() => navigate('/dashboard/players')}
              onMouseEnter={e => e.currentTarget.style.borderColor='var(--border-light)'}
              onMouseLeave={e => e.currentTarget.style.borderColor='var(--border)'}>
              <div style={{ fontSize:'1.5rem', marginBottom:'12px' }}>⚾</div>
              <div style={{ fontWeight:'600', color:'var(--text-primary)', marginBottom:'4px' }}>Manage Players</div>
              <div style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>Add or update your athletes</div>
            </div>
            <div className="card" style={{ cursor:'pointer', transition:'border-color var(--transition)' }} onClick={() => navigate('/dashboard/profile')}
              onMouseEnter={e => e.currentTarget.style.borderColor='var(--border-light)'}
              onMouseLeave={e => e.currentTarget.style.borderColor='var(--border)'}>
              <div style={{ fontSize:'1.5rem', marginBottom:'12px' }}>👤</div>
              <div style={{ fontWeight:'600', color:'var(--text-primary)', marginBottom:'4px' }}>My Profile</div>
              <div style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>Update your contact info</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ---- My Sessions ----
function MySessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [cancelSession, setCancelSession] = useState(null);
  const [detailSession, setDetailSession] = useState(null);
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [pastPage, setPastPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get('/api/booking/my-sessions'); setSessions(r.data.sessions); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const exportCSV = () => {
    const headers = ['Date','Time','Instructor','Player','Status','Notes'];
    const rows = sessions.map(s => [
      s.session_date.split('T')[0],
      formatTime(s.start_time) + ' - ' + formatTime(s.end_time),
      s.instructor_first + ' ' + s.instructor_last,
      s.player_first ? s.player_first + ' ' + s.player_last : '',
      s.status,
      (s.notes || '').replace(/,/g, ';'),
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type:'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'my-sessions.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const filtered = sessions.filter(s => !statusFilter || s.status === statusFilter);
  const todayStart = new Date(); todayStart.setHours(0,0,0,0);
  const upcoming = filtered.filter(s => {
    if (s.status === 'cancelled' || s.status === 'completed') return false;
    const parts = s.session_date.split('T')[0].split('-');
    const sessionDate = new Date(parseInt(parts[0]), parseInt(parts[1])-1, parseInt(parts[2]));
    return sessionDate >= todayStart;
  });
  const past = filtered.filter(s => {
    if (s.status === 'cancelled' || s.status === 'completed') return true;
    const parts = s.session_date.split('T')[0].split('-');
    const sessionDate = new Date(parseInt(parts[0]), parseInt(parts[1])-1, parseInt(parts[2]));
    return sessionDate < todayStart;
  });

  useEffect(() => { setUpcomingPage(1); setPastPage(1); }, [statusFilter]);
  const upcomingTotal = Math.max(1, Math.ceil(upcoming.length / perPage));
  const upcomingPaginated = upcoming.slice((upcomingPage-1)*perPage, upcomingPage*perPage);
  const pastTotal = Math.max(1, Math.ceil(past.length / perPage));
  const pastPaginated = past.slice((pastPage-1)*perPage, pastPage*perPage);

  return (
    <div className="page">
      <div className="page-header" style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
        <div><h1 className="page-title">MY SESSIONS</h1><p className="page-subtitle">Your upcoming and past training sessions</p></div>
        <button className="btn btn-outline btn-sm" onClick={exportCSV} style={{ marginTop:'4px' }}>↓ Export CSV</button>
      </div>

      <div style={{ display:'flex', gap:'8px', marginBottom:'20px', flexWrap:'wrap', alignItems:'center' }}>
        {['','pending','confirmed','completed','cancelled'].map(s => (
          <button key={s} className={`badge ${statusFilter === s ? 'badge-info' : 'badge-default'}`}
            style={{ cursor:'pointer', border:'none', padding:'6px 14px', fontSize:'0.8rem' }}
            onClick={() => setStatusFilter(s)}>
            {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
        <select style={{ marginLeft:'auto', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', color:'var(--text-secondary)', fontSize:'0.8rem', padding:'5px 10px', cursor:'pointer' }} value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setUpcomingPage(1); setPastPage(1); }}>
          <option value={10}>10 / page</option>
          <option value={25}>25 / page</option>
          <option value={50}>50 / page</option>
        </select>
      </div>

      {loading ? <div className="empty-state"><span className="empty-state-text">Loading...</span></div> : (
        <>
          <div className="table-container" style={{ marginBottom:'24px' }}>
            <div className="table-toolbar"><span className="table-toolbar-title">Upcoming</span></div>
            {upcoming.length === 0 ? (
              <div className="empty-state">
                <span className="empty-state-icon">📅</span>
                <span className="empty-state-text">No upcoming sessions.</span>
                <a href="/dashboard/book" className="btn btn-primary btn-sm">Book Now</a>
              </div>
            ) : (
              <table>
                <thead><tr><th>Instructor</th><th>Date</th><th>Time</th><th>Player</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {upcomingPaginated.map(s => (
                    <tr key={s.id}>
                      <td>
                        <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                          <InstructorAvatar photo={s.instructor_photo} first={s.instructor_first} last={s.instructor_last} size={36} />
                          <span className="td-primary">{s.instructor_first} {s.instructor_last}</span>
                        </div>
                      </td>
                      <td>{formatDate(s.session_date)}</td>
                      <td>{formatTime(s.start_time)} – {formatTime(s.end_time)}</td>
                      <td>{s.player_first ? `${s.player_first} ${s.player_last}` : '—'}</td>
                      <td><span className={`badge ${STATUS_COLORS[s.status]||'badge-default'}`}>{s.status}</span></td>
                      <td>
                        <div style={{ display:'flex', gap:'6px' }}>
                          <button className="badge badge-default" style={{ cursor:'pointer', border:'none' }} onClick={() => setDetailSession(s)}>View</button>
                          {s.status !== 'cancelled' && (
                            <button className="badge badge-error" style={{ cursor:'pointer', border:'none' }} onClick={() => setCancelSession(s)}>Cancel</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {upcoming.length > 0 && (
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 20px', borderTop:'1px solid var(--border)', flexWrap:'wrap', gap:'12px' }}>
                <span style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>
                  Showing {Math.min((upcomingPage-1)*perPage+1, upcoming.length)}–{Math.min(upcomingPage*perPage, upcoming.length)} of {upcoming.length}
                </span>
                {upcomingTotal > 1 && (
                  <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                    <button className="btn btn-outline btn-sm" onClick={() => setUpcomingPage(p => Math.max(1, p-1))} disabled={upcomingPage === 1}>← Prev</button>
                    <span style={{ fontSize:'0.85rem', color:'var(--text-secondary)', minWidth:'90px', textAlign:'center' }}>Page {upcomingPage} of {upcomingTotal}</span>
                    <button className="btn btn-outline btn-sm" onClick={() => setUpcomingPage(p => Math.min(upcomingTotal, p+1))} disabled={upcomingPage === upcomingTotal}>Next →</button>
                  </div>
                )}
              </div>
            )}
          </div>

          {past.length > 0 && (
            <div className="table-container">
              <div className="table-toolbar"><span className="table-toolbar-title">Past Sessions</span></div>
              <table>
                <thead><tr><th>Instructor</th><th>Date</th><th>Time</th><th>Player</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {pastPaginated.map(s => (
                    <tr key={s.id}>
                      <td>
                        <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                          <InstructorAvatar photo={s.instructor_photo} first={s.instructor_first} last={s.instructor_last} size={36} />
                          <span className="td-primary">{s.instructor_first} {s.instructor_last}</span>
                        </div>
                      </td>
                      <td>{formatDate(s.session_date)}</td>
                      <td>{formatTime(s.start_time)} – {formatTime(s.end_time)}</td>
                      <td>{s.player_first ? `${s.player_first} ${s.player_last}` : '—'}</td>
                      <td><span className={`badge ${STATUS_COLORS[s.status]||'badge-default'}`}>{s.status}</span></td>
                      <td><button className="badge badge-default" style={{ cursor:'pointer', border:'none' }} onClick={() => setDetailSession(s)}>View</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 20px', borderTop:'1px solid var(--border)', flexWrap:'wrap', gap:'12px' }}>
                <span style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>
                  Showing {Math.min((pastPage-1)*perPage+1, past.length)}–{Math.min(pastPage*perPage, past.length)} of {past.length}
                </span>
                {pastTotal > 1 && (
                  <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                    <button className="btn btn-outline btn-sm" onClick={() => setPastPage(p => Math.max(1, p-1))} disabled={pastPage === 1}>← Prev</button>
                    <span style={{ fontSize:'0.85rem', color:'var(--text-secondary)', minWidth:'90px', textAlign:'center' }}>Page {pastPage} of {pastTotal}</span>
                    <button className="btn btn-outline btn-sm" onClick={() => setPastPage(p => Math.min(pastTotal, p+1))} disabled={pastPage === pastTotal}>Next →</button>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {cancelSession && <CancelModal session={cancelSession} onClose={() => setCancelSession(null)} onCancelled={fetchSessions} />}
      {detailSession && <SessionDetailModal session={detailSession} onClose={() => setDetailSession(null)} onCancelRequest={s => { setDetailSession(null); setCancelSession(s); }} />}
    </div>
  );
}

// ---- Month Calendar for Booking ----
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DOW_LABELS = ['Mo','Tu','We','Th','Fr','Sa','Su'];

function MonthCalendar({ selectedDate, onSelectDate, todayStr, nextWeekEndStr, availableDays, fullDayExceptions }) {
  const [viewYear, setViewYear] = useState(() => Number(todayStr.slice(0, 4)));
  const [viewMonth, setViewMonth] = useState(() => Number(todayStr.slice(5, 7)) - 1);

  const prevMonth = () => { if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); } else setViewMonth(m => m - 1); };
  const nextMonth = () => { if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); } else setViewMonth(m => m + 1); };
  const goToday = () => {
    setViewYear(Number(todayStr.slice(0, 4)));
    setViewMonth(Number(todayStr.slice(5, 7)) - 1);
    const todayDow = new Date(todayStr + 'T00:00:00Z').getUTCDay();
    if (availableDays?.includes(todayDow) && !fullDayExceptions.includes(todayStr)) onSelectDate(todayStr);
  };

  const toStr = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

  const getDays = () => {
    const firstDay = new Date(viewYear, viewMonth, 1);
    const lastDay = new Date(viewYear, viewMonth + 1, 0);
    const startDow = (firstDay.getDay() + 6) % 7; // Mon=0, Sun=6
    const days = [];
    for (let i = 0; i < startDow; i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) days.push(new Date(viewYear, viewMonth, d));
    while (days.length % 7 !== 0) days.push(null);
    return days;
  };

  const days = getDays();

  return (
    <div className="booking-calendar">
      <div className="booking-calendar-toolbar">
        <button type="button" onClick={prevMonth} aria-label="Previous month">‹</button>
        <span className="booking-calendar-month">{MONTH_NAMES[viewMonth]} {viewYear}</span>
        <button type="button" onClick={goToday} className="booking-calendar-today">Today</button>
        <button type="button" onClick={nextMonth} aria-label="Next month">›</button>
      </div>
      <div className="booking-calendar-grid" style={{ marginBottom:'4px' }}>
        {DOW_LABELS.map(l => <div key={l} style={{ textAlign:'center', fontSize:'0.62rem', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.08em', fontWeight:'600', padding:'3px 0' }}>{l}</div>)}
      </div>
      <div className="booking-calendar-grid">
        {days.map((d, i) => {
          if (!d) return <div key={i} />;
          const str = toStr(d);
          const dayDow = d.getDay();
          const isPast = str < todayStr;
          const isBeyondWindow = str > nextWeekEndStr;
          const isToday = str === todayStr;
          const isSelected = str === selectedDate;
          const scheduleLoading = availableDays === null;
          const noSlotForDay = !scheduleLoading && !availableDays.includes(dayDow);
          const isBlocked = fullDayExceptions.includes(str);
          const isFutureLocked = isBeyondWindow || scheduleLoading || noSlotForDay || isBlocked;
          const disabled = isPast || isFutureLocked;

          let tooltipText = '';
          if (isPast) tooltipText = 'Date has passed';
          else if (isBeyondWindow) tooltipText = 'Bookings limited to current and next week';
          else if (scheduleLoading) tooltipText = 'Loading availability...';
          else if (isBlocked) tooltipText = 'Unavailable — blocked by instructor';
          else if (noSlotForDay) tooltipText = 'No availability set for this day';

          let bg = 'transparent', color = 'var(--text-primary)', border = '1px solid transparent', opacity = 1, cursor = 'pointer';
          if (isSelected)       { bg = 'var(--text-primary)'; color = 'var(--bg-primary)'; border = '2px solid var(--text-primary)'; }
          else if (isToday)     { border = '1px solid var(--text-primary)'; }
          if (disabled) { opacity = 0.28; cursor = 'default'; color = 'var(--text-muted)'; }

          return (
            <button key={i} type="button" disabled={disabled}
              title={tooltipText}
              className="booking-calendar-day"
              aria-label={formatDateFull(str)}
              aria-pressed={isSelected}
              aria-current={isToday ? 'date' : undefined}
              onClick={() => !disabled && onSelectDate(str)}
              style={{ padding:'6px 2px', borderRadius:'6px', textAlign:'center', background:bg, color, border, opacity, cursor, fontWeight: isSelected || isToday ? '700' : '400' }}>
              <div style={{ fontSize:'0.82rem' }}>{d.getDate()}</div>
              {isFutureLocked && !isBeyondWindow && <div style={{ fontSize:'0.5rem', lineHeight:1, marginTop:'1px', opacity:0.6 }}>🔒</div>}
            </button>
          );
        })}
      </div>
      <p style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:'8px', lineHeight:'1.5' }}>
        Book this week or next week. Dimmed dates are unavailable. All times are Eastern Time.
      </p>
    </div>
  );
}

// ---- Book a Session ----
function BookSession() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [instructors, setInstructors] = useState([]);
  const [players, setPlayers] = useState([]);
  const [playersLoaded, setPlayersLoaded] = useState(false);
  const [selected, setSelected] = useState({ instructor: null, date: '', slots: [], slot: null, player: null, notes: '', duration: null });
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [booking, setBooking] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [expandedInstructor, setExpandedInstructor] = useState(null);
  const [instructorDetail, setInstructorDetail] = useState(null);
  const [scheduleWindow, setScheduleWindow] = useState(null);
  const [scheduleError, setScheduleError] = useState('');
  const [slotsError, setSlotsError] = useState('');
  const [availabilityAttempt, setAvailabilityAttempt] = useState(0);
  const instructorId = selected.instructor?.id;
  const selectedDate = selected.date;
  const selectedDuration = selected.duration;
  const bookingWindow = getBookingWindow();
  const todayStr = scheduleWindow?.today || bookingWindow.today;
  const nextWeekEndStr = scheduleWindow?.next_week_end || bookingWindow.next_week_end;

  useEffect(() => {
    if (!instructorId) return;
    let cancelled = false;
    setScheduleError('');
    axios.get(`/api/booking/instructors/${instructorId}/schedule-window`)
      .then(r => {
        if (cancelled) return;
        setScheduleWindow(r.data);
        const today = r.data.today || getBookingWindow().today;
        const dow = new Date(today + 'T00:00:00Z').getUTCDay();
        if (r.data.available_days.includes(dow) && !r.data.full_day_exceptions.includes(today)) {
          setSelected(s => s.date ? s : { ...s, date: today });
        }
      })
      .catch(() => {
        if (!cancelled) setScheduleError('Unable to load the coach’s schedule. Please try again.');
      });
    return () => { cancelled = true; };
  }, [instructorId, availabilityAttempt]);

  useEffect(() => {
    if (!instructorId || !selectedDate || !selectedDuration) return;
    let cancelled = false;
    setLoadingSlots(true);
    setSlotsError('');
    axios.get(`/api/booking/instructors/${instructorId}/slots`, {
      params: { date: selectedDate, duration: selectedDuration },
    }).then(r => {
      if (!cancelled) setSelected(s => ({ ...s, slots: r.data.slots || [] }));
    }).catch(() => {
      if (!cancelled) setSlotsError('Unable to load available times. Please try again.');
    }).finally(() => {
      if (!cancelled) setLoadingSlots(false);
    });
    return () => { cancelled = true; };
  }, [instructorId, selectedDate, selectedDuration, availabilityAttempt]);

  useEffect(() => {
    axios.get('/api/booking/instructors').then(r => setInstructors(r.data.instructors)).catch(console.error);
    axios.get('/api/booking/players')
      .then(r => setPlayers(r.data.players))
      .catch(console.error)
      .finally(() => setPlayersLoaded(true));
  }, []);

  const loadInstructorDetail = async (id) => {
    if (expandedInstructor === id) { setExpandedInstructor(null); setInstructorDetail(null); return; }
    try {
      const r = await axios.get(`/api/parent/instructors/${id}`);
      setInstructorDetail(r.data.instructor);
      setExpandedInstructor(id);
    } catch (err) { console.error(err); }
  };

  const selectInstructor = instructor => {
    const defaultDuration = instructor.offers_30_min && instructor.offers_60_min
      ? (instructor.session_duration_minutes === 60 ? 60 : 30)
      : instructor.offers_30_min ? 30 : instructor.offers_60_min ? 60 : instructor.session_duration_minutes;
    setSelected(s => ({...s, instructor, date:'', slots:[], slot:null, duration: defaultDuration}));
    setScheduleWindow(null);
    setScheduleError(''); setSlotsError('');
    setAvailabilityAttempt(a => a + 1);
    setStep(2); setExpandedInstructor(null); setInstructorDetail(null);
  };

  const selectDate = date => {
    if (date === selected.date) return;
    setSelected(s => ({...s, date, slots:[], slot:null}));
  };

  const selectSlot = slot => { setSelected(s => ({...s, slot})); setStep(3); };

  const confirm = async () => {
    setBooking(true); setError('');
    try {
      await axios.post('/api/booking/sessions', {
        instructor_id: selected.instructor.id,
        duration_minutes: selected.duration || selected.instructor.session_duration_minutes,
        player_id: selected.player || undefined,
        session_date: selected.date,
        start_time: selected.slot.start_time,
        end_time: selected.slot.end_time,
        notes: selected.notes || undefined,
      });
      setSuccess(true);
    } catch (err) { setError(err.response?.data?.error || 'Booking failed. Please try again.'); }
    finally { setBooking(false); }
  };

  // Block booking until a player is added
  if (playersLoaded && players.length === 0) return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">BOOK A SESSION</h1>
      </div>
      <div style={{ maxWidth:'520px', margin:'0 auto' }}>
        <div style={{ background:'rgba(250,204,21,0.08)', border:'1px solid rgba(250,204,21,0.25)', borderRadius:'var(--radius-lg)', padding:'28px 32px', textAlign:'center' }}>
          <div style={{ fontSize:'2.5rem', marginBottom:'16px' }}>⚾</div>
          <h2 style={{ fontFamily:'var(--font-display)', fontSize:'1.4rem', letterSpacing:'0.05em', marginBottom:'12px', color:'var(--text-primary)' }}>ADD A PLAYER FIRST</h2>
          <p style={{ color:'var(--text-secondary)', lineHeight:'1.7', marginBottom:'8px' }}>
            You need to add at least one player to your account before you can book a training session.
          </p>
          <p style={{ color:'var(--text-muted)', fontSize:'0.85rem', lineHeight:'1.6', marginBottom:'28px' }}>
            Head to <strong style={{ color:'var(--text-secondary)' }}>My Players</strong> to add your child's information, then come back here to book.
            Need help? Check the{' '}
            <a href="/help/parent" target="_blank" rel="noopener noreferrer" style={{ color:'var(--accent)', textDecoration:'none', fontWeight:'600' }}>
              Parent How-To Guide
            </a>
            {' '}— the <em>Managing Your Players</em> section walks you through it step by step.
          </p>
          <div style={{ display:'flex', gap:'12px', justifyContent:'center', flexWrap:'wrap' }}>
            <button className="btn btn-primary" onClick={() => navigate('/dashboard/players')}>
              Go to My Players
            </button>
            <a href="/help/parent" target="_blank" rel="noopener noreferrer" className="btn btn-outline">
              View How-To Guide
            </a>
          </div>
        </div>
      </div>
    </div>
  );

  if (success) return (
    <div className="page">
      <div style={{ maxWidth:'480px', margin:'0 auto', padding:'24px 0' }}>
        <div style={{ textAlign:'center', marginBottom:'28px' }}>
          <div style={{ width:'72px', height:'72px', borderRadius:'50%', background:'rgba(74,222,128,0.15)', border:'2px solid #4ade80', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'2rem', margin:'0 auto 16px' }}>✓</div>
          <h2 style={{ fontFamily:'var(--font-display)', fontSize:'2rem', letterSpacing:'0.05em', marginBottom:'10px' }}>SESSION BOOKED!</h2>
          <p style={{ color:'var(--text-secondary)', lineHeight:'1.6', marginBottom:'6px' }}>Your session with <strong style={{ color:'var(--text-primary)' }}>{selected.instructor.first_name} {selected.instructor.last_name}</strong> on {formatDateFull(selected.date)} at {formatTime(selected.slot.start_time)} has been requested.</p>
          <p style={{ color:'var(--text-muted)', fontSize:'0.85rem' }}>Your instructor will confirm the session shortly.</p>
        </div>

        <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'24px', marginBottom:'20px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px' }}>
            <span style={{ fontSize:'1.2rem' }}>💸</span>
            <span style={{ fontFamily:'var(--font-display)', fontSize:'1.1rem', letterSpacing:'0.05em', color:'var(--text-primary)' }}>PAYMENT</span>
          </div>
          <p style={{ color:'var(--text-muted)', fontSize:'0.82rem', marginBottom:'20px' }}>
            Payment is due before your session begins. Scan the QR code below with CashApp or tap the button to pay directly.
          </p>
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'14px' }}>
            <img src="/api/cashapp-qr" alt="CashApp QR Code" style={{ width:'200px', height:'227px', borderRadius:'8px', border:'1px solid var(--border)' }} />
            <div style={{ textAlign:'center' }}>
              <p style={{ color:'var(--text-secondary)', fontSize:'0.875rem', marginBottom:'4px' }}>Open CashApp and scan the QR code above</p>
              <p style={{ color:'var(--text-muted)', fontSize:'0.8rem' }}>or search <strong style={{ color:'var(--text-primary)' }}>$WowFactorCrawford</strong> in CashApp</p>
            </div>
            <a href="https://cash.app/$WowFactorCrawford" target="_blank" rel="noopener noreferrer"
              style={{ display:'inline-flex', alignItems:'center', gap:'8px', background:'#00D632', color:'#000', padding:'12px 28px', borderRadius:'8px', fontWeight:'700', fontSize:'0.9rem', textDecoration:'none' }}>
              💸 Pay with CashApp
            </a>
          </div>
        </div>

        <div style={{ display:'flex', gap:'12px', justifyContent:'center', flexWrap:'wrap' }}>
          <button className="btn btn-outline" onClick={() => { setSuccess(false); setStep(1); setSelected({ instructor:null, date:'', slots:[], slot:null, player:null, notes:'' }); }}>Book Another</button>
          <a href="/dashboard/sessions" className="btn btn-primary">View My Sessions</a>
        </div>
      </div>
    </div>
  );

  return (
    <div className="page">
      <div className="page-header"><h1 className="page-title">BOOK A SESSION</h1><p className="page-subtitle">Choose an instructor and pick a time that works for you</p></div>

      {/* Progress */}
      <div style={{ display:'flex', gap:'8px', marginBottom:'32px', alignItems:'center' }}>
        {['Instructor','Date & Time','Confirm'].map((label,i) => (
          <div key={i} style={{ display:'flex', alignItems:'center', gap:'8px' }}>
            <div style={{ width:'28px', height:'28px', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.75rem', fontWeight:'700', background: step > i+1 ? 'var(--success)' : step === i+1 ? 'var(--text-primary)' : 'var(--bg-elevated)', color: step >= i+1 ? 'var(--bg-primary)' : 'var(--text-muted)', border: step <= i+1 ? '1px solid var(--border)' : 'none' }}>{step > i+1 ? '✓' : i+1}</div>
            <span style={{ fontSize:'0.8rem', color: step === i+1 ? 'var(--text-primary)' : 'var(--text-muted)', fontWeight: step === i+1 ? '600' : '400' }}>{label}</span>
            {i < 2 && <span style={{ color:'var(--border-light)', margin:'0 4px' }}>›</span>}
          </div>
        ))}
      </div>

      {/* Step 1 - Pick Instructor */}
      {step === 1 && (
        <div>
          {instructors.length === 0 ? (
            <div className="empty-state"><span className="empty-state-icon">👤</span><span className="empty-state-text">No instructors available at this time.</span></div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
              {instructors.map(inst => (
                <div key={inst.id} className="card" style={{ transition:'border-color var(--transition)' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'16px', flexWrap:'wrap' }}>
                    <InstructorAvatar photo={inst.photo_url} first={inst.first_name} last={inst.last_name} size={64} />
                    <div style={{ flex:1, minWidth:'200px' }}>
                      <div style={{ fontWeight:'600', color:'var(--text-primary)', fontSize:'1.05rem' }}>{inst.first_name} {inst.last_name}</div>
                      <div style={{ fontSize:'0.8rem', color:'var(--text-muted)', marginBottom:'6px' }}>{inst.session_duration_minutes} min sessions</div>
                      {inst.specialties?.length > 0 && (
                        <div style={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
                          {inst.specialties.map(s => <span key={s} className="badge badge-default">{s}</span>)}
                        </div>
                      )}
                    </div>
                    <div style={{ display:'flex', gap:'8px', flexShrink:0 }}>
                      <button className="btn btn-outline btn-sm" onClick={() => loadInstructorDetail(inst.id)}>
                        {expandedInstructor === inst.id ? 'Hide Info' : 'View Profile'}
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={() => selectInstructor(inst)}>Select</button>
                    </div>
                  </div>

                  {/* Expanded instructor detail */}
                  {expandedInstructor === inst.id && instructorDetail && (
                    <div style={{ marginTop:'16px', paddingTop:'16px', borderTop:'1px solid var(--border)' }}>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px' }}>
                        {instructorDetail.bio && (
                          <div>
                            <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.08em', fontWeight:'600', marginBottom:'6px' }}>About</div>
                            <p style={{ fontSize:'0.875rem', color:'var(--text-secondary)', lineHeight:'1.6' }}>{instructorDetail.bio}</p>
                          </div>
                        )}
                        <div>
                          <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.08em', fontWeight:'600', marginBottom:'8px' }}>Details</div>
                          {[
                            ['Session Length', `${instructorDetail.session_duration_minutes} minutes`],
                            ['Sessions Completed', instructorDetail.completed_sessions],
                          ].map(([label, value]) => (
                            <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', borderBottom:'1px solid var(--border)' }}>
                              <span style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>{label}</span>
                              <span style={{ fontSize:'0.8rem', color:'var(--text-primary)', fontWeight:'500' }}>{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 2 - Pick Date & Time */}
      {step === 2 && selected.instructor && (
        <div>
          <button className="btn btn-ghost btn-sm" style={{ marginBottom:'20px' }} onClick={() => setStep(1)}>← Back</button>
          <div style={{ padding:'16px', background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', marginBottom:'24px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'16px', marginBottom: (selected.instructor.offers_30_min && selected.instructor.offers_60_min) ? '16px' : '0' }}>
              <InstructorAvatar photo={selected.instructor.photo_url} first={selected.instructor.first_name} last={selected.instructor.last_name} size={48} />
              <div>
                <div style={{ fontWeight:'600', color:'var(--text-primary)' }}>{selected.instructor.first_name} {selected.instructor.last_name}</div>
                <div style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>
                  {selected.instructor.offers_30_min && selected.instructor.offers_60_min ? 'Offers 30 & 60 min sessions' :
                   selected.instructor.offers_30_min ? '30 min sessions' : '60 min sessions'}
                </div>
              </div>
            </div>
            {selected.instructor.offers_30_min && selected.instructor.offers_60_min && (
              <div>
                <div style={{ fontSize:'0.8rem', color:'var(--text-muted)', marginBottom:'8px', textTransform:'uppercase', letterSpacing:'0.05em', fontWeight:'600' }}>Session Length</div>
                <div style={{ display:'flex', gap:'10px' }}>
                  {[{val:30,label:'30 Minutes'},{val:60,label:'60 Minutes'}].map(opt => (
                    <button key={opt.val} onClick={() => {
                      if (selected.duration === opt.val) return;
                      setSelected(s => ({...s, duration: opt.val, slots:[], slot:null}));
                    }}
                    aria-pressed={selected.duration === opt.val}
                    style={{ flex:1, padding:'10px', borderRadius:'var(--radius-md)',
                      border: selected.duration === opt.val ? '2px solid var(--text-primary)' : '1px solid var(--border)',
                      background: selected.duration === opt.val ? 'var(--text-primary)' : 'var(--bg-elevated)',
                      color: selected.duration === opt.val ? 'var(--bg-primary)' : 'var(--text-primary)',
                      fontWeight:'600', fontSize:'0.9rem', cursor:'pointer' }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="booking-date-time-grid">
            <div className="card booking-date-card">
              <div className="stat-card-label" style={{ marginBottom:'12px' }}>Select a Date</div>
              <MonthCalendar
                selectedDate={selected.date}
                onSelectDate={(dateStr) => selectDate(dateStr)}
                todayStr={todayStr}
                nextWeekEndStr={nextWeekEndStr}
                availableDays={scheduleWindow?.available_days ?? null}
                fullDayExceptions={scheduleWindow?.full_day_exceptions ?? []}
              />
              {scheduleError && (
                <div role="alert" className="booking-load-error">
                  <p>{scheduleError}</p>
                  <button className="btn btn-outline btn-sm" onClick={() => setAvailabilityAttempt(a => a + 1)}>Retry</button>
                </div>
              )}
            </div>
            <div className="card booking-times-card" aria-live="polite" aria-busy={loadingSlots}>
              <div className="stat-card-label" style={{ marginBottom:'12px' }}>Available Times</div>
              {selected.date && <p className="booking-times-date">{formatDateFull(selected.date)} · {selected.duration} min · ET</p>}
              {!selected.date ? (
                <p style={{ color:'var(--text-muted)', fontSize:'0.875rem' }}>Select a date to see available times</p>
              ) : loadingSlots ? (
                <p style={{ color:'var(--text-muted)', fontSize:'0.875rem' }}>Loading...</p>
              ) : slotsError ? (
                <div role="alert" className="booking-load-error">
                  <p>{slotsError}</p>
                  <button className="btn btn-outline btn-sm" onClick={() => setAvailabilityAttempt(a => a + 1)}>Retry</button>
                </div>
              ) : selected.slots.length === 0 ? (
                <p style={{ color:'var(--text-muted)', fontSize:'0.875rem' }}>No available times on this date. Try another day.</p>
              ) : (
                <div className="booking-times-grid">
                  {selected.slots.map((slot,i) => (
                    <button key={i} onClick={() => selectSlot(slot)}
                      style={{ padding:'10px 8px', borderRadius:'var(--radius-md)', border:'1px solid var(--border)', background:'var(--bg-elevated)', color:'var(--text-primary)', fontSize:'0.8rem', fontWeight:'600', cursor:'pointer', transition:'all var(--transition)' }}
                      onMouseEnter={e => { e.currentTarget.style.background='var(--text-primary)'; e.currentTarget.style.color='var(--bg-primary)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background='var(--bg-elevated)'; e.currentTarget.style.color='var(--text-primary)'; }}>
                      {formatTime(slot.start_time)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Step 3 - Confirm */}
      {step === 3 && selected.slot && (
        <div style={{ maxWidth:'520px' }}>
          <button className="btn btn-ghost btn-sm" style={{ marginBottom:'20px' }} onClick={() => setStep(2)}>← Back</button>
          <div className="card" style={{ marginBottom:'20px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'16px', marginBottom:'16px', paddingBottom:'16px', borderBottom:'1px solid var(--border)' }}>
              <InstructorAvatar photo={selected.instructor.photo_url} first={selected.instructor.first_name} last={selected.instructor.last_name} size={56} />
              <div>
                <div style={{ fontWeight:'600', color:'var(--text-primary)', fontSize:'1rem' }}>{selected.instructor.first_name} {selected.instructor.last_name}</div>
                <div style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>Instructor</div>
              </div>
            </div>
            <div className="stat-card-label" style={{ marginBottom:'16px' }}>Booking Summary</div>
            <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
              {[
                ['Date', formatDateFull(selected.date)],
                ['Time', `${formatTime(selected.slot.start_time)} – ${formatTime(selected.slot.end_time)}`],
                ['Duration', `${selected.duration || selected.instructor.session_duration_minutes} minutes`],
              ].map(([label, value]) => (
                <div key={label} style={{ display:'flex', justifyContent:'space-between', paddingBottom:'10px', borderBottom:'1px solid var(--border)' }}>
                  <span style={{ fontSize:'0.8rem', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.08em', fontWeight:'600' }}>{label}</span>
                  <span style={{ fontSize:'0.9rem', color:'var(--text-primary)', fontWeight:'500' }}>{value}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>
            {players.length > 0 && (
              <div className="form-group">
                <label className="form-label">Player (optional)</label>
                <select className="form-input" value={selected.player || ''} onChange={e => setSelected(s => ({...s, player: e.target.value || null}))}>
                  <option value="">Select a player...</option>
                  {players.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
                </select>
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Notes (optional)</label>
              <textarea className="form-input" rows={3} placeholder="Any specific goals or things to work on..." value={selected.notes} onChange={e => setSelected(s => ({...s, notes: e.target.value}))} style={{ resize:'vertical' }} />
            </div>
            {error && <div className="auth-error-banner"><span>⚠</span> {error}</div>}
            <button className="btn btn-primary btn-lg" onClick={confirm} disabled={booking} style={{ width:'100%' }}>
              {booking ? 'Booking...' : 'Confirm Booking'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- My Players ----
function MyPlayers() {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const emptyForm = { first_name:'', last_name:'', date_of_birth:'', positions:[], notes:'' };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [photoError, setPhotoError] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const POSITIONS = ['Pitcher','Catcher','First Base','Second Base','Third Base','Shortstop','Left Field','Center Field','Right Field','Designated Hitter','Utility'];

  const fetchPlayers = async () => {
    setLoading(true);
    try { const r = await axios.get('/api/booking/players'); setPlayers(r.data.players); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPlayers(); }, []);

  const openAdd = () => { setEditingPlayer(null); setForm(emptyForm); setError(''); setShowModal(true); };
  const openEdit = p => {
    setEditingPlayer(p);
    setForm({
      first_name: p.first_name,
      last_name: p.last_name,
      date_of_birth: p.date_of_birth ? p.date_of_birth.split('T')[0] : '',
      positions: p.position ? p.position.split(', ').filter(Boolean) : [],
      notes: p.notes || '',
    });
    setError('');
    setShowModal(true);
  };

  const togglePosition = pos => setForm(f => ({
    ...f,
    positions: f.positions.includes(pos) ? f.positions.filter(x => x !== pos) : [...f.positions, pos]
  }));

  const handleSubmit = async e => {
    e.preventDefault(); setSaving(true); setError('');
    const payload = { ...form, position: form.positions.join(', ') };
    try {
      if (editingPlayer) {
        await axios.patch(`/api/booking/players/${editingPlayer.id}`, payload);
      } else {
        await axios.post('/api/booking/players', payload);
      }
      await fetchPlayers();
      setShowModal(false);
    } catch (err) { setError(err.response?.data?.error || 'Failed to save player.'); }
    finally { setSaving(false); }
  };

  const uploadPlayerPhoto = async (playerId, file) => {
    if (!file) return;
    setPhotoError('');
    setPhotoUploading(true);
    const formData = new FormData();
    formData.append('photo', file);
    try {
      const r = await axios.post(`/api/booking/players/${playerId}/photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (editingPlayer?.id === playerId) setEditingPlayer(p => ({ ...p, photo_url: r.data.photo_url }));
      fetchPlayers();
    } catch (err) {
      setPhotoError(err.response?.data?.error || 'Failed to upload photo. Please try again.');
    } finally { setPhotoUploading(false); }
  };

  const removePlayerPhoto = async playerId => {
    setPhotoError('');
    try {
      await axios.delete(`/api/booking/players/${playerId}/photo`);
      if (editingPlayer?.id === playerId) setEditingPlayer(p => ({ ...p, photo_url: null }));
      fetchPlayers();
    } catch (err) { setPhotoError(err.response?.data?.error || 'Failed to remove photo.'); }
  };

  const deletePlayer = async id => {
    if (!window.confirm('Remove this player?')) return;
    try { await axios.delete(`/api/booking/players/${id}`); await fetchPlayers(); }
    catch (err) { console.error(err); }
  };

  return (
    <div className="page">
      <div className="page-header" style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
        <div><h1 className="page-title">MY PLAYERS</h1><p className="page-subtitle">Manage your athletes</p></div>
        <button className="btn btn-primary btn-sm" onClick={openAdd}>+ Add Player</button>
      </div>
      {loading ? <div className="empty-state"><span className="empty-state-text">Loading...</span></div>
      : players.length === 0 ? (
        <div className="empty-state" style={{ padding:'60px', background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)' }}>
          <span className="empty-state-icon">⚾</span>
          <span className="empty-state-text">No players added yet.</span>
          <button className="btn btn-primary btn-sm" onClick={openAdd}>Add Your First Player</button>
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(260px,1fr))', gap:'16px' }}>
          {players.map(p => (
            <div key={p.id} className="card">
              <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'12px' }}>
                <div style={{ position:'relative', width:'48px', height:'48px', flexShrink:0 }}>
                  {p.photo_url ? (
                    <img src={p.photo_url} alt={p.first_name} style={{ width:'48px', height:'48px', borderRadius:'50%', objectFit:'cover', border:'2px solid var(--border-light)' }} />
                  ) : (
                    <div style={{ width:'48px', height:'48px', borderRadius:'50%', background:'var(--bg-elevated)', border:'1px solid var(--border-light)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:'700', color:'var(--text-secondary)' }}>
                      {p.first_name[0]}{p.last_name[0]}
                    </div>
                  )}
                  <label htmlFor={`pp-${p.id}`} style={{ position:'absolute', bottom:'-2px', right:'-2px', width:'18px', height:'18px', borderRadius:'50%', background:'var(--text-primary)', border:'2px solid var(--bg-card)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:'0.55rem', color:'var(--bg-primary)', fontWeight:'900' }} title="Upload photo">+</label>
                  <input id={`pp-${p.id}`} type="file" accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif" style={{ display:'none' }} onChange={e => { uploadPlayerPhoto(p.id, e.target.files[0]); e.target.value = ''; }} />
                </div>
                <div>
                  <div style={{ fontWeight:'600', color:'var(--text-primary)' }}>{p.first_name} {p.last_name}</div>
                  {p.position && <div style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>{p.position}</div>}
                </div>
              </div>
              {p.date_of_birth && <p style={{ fontSize:'0.8rem', color:'var(--text-muted)', marginBottom:'8px' }}>DOB: {new Date(p.date_of_birth.split('T')[0]+'T12:00:00').toLocaleDateString()}</p>}
              {p.notes && <p style={{ fontSize:'0.85rem', color:'var(--text-secondary)', marginBottom:'12px' }}>{p.notes}</p>}
              <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
                <button className="btn btn-outline btn-sm" style={{ fontSize:'0.75rem' }} onClick={() => openEdit(p)}>Edit</button>
                {p.photo_url && (
                  <button className="btn btn-ghost btn-sm" style={{ fontSize:'0.75rem', color:'var(--text-muted)' }} onClick={() => removePlayerPhoto(p.id)}>Remove Photo</button>
                )}
                <button className="btn btn-ghost btn-sm" style={{ color:'var(--error)' }} onClick={() => deletePlayer(p.id)}>Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{editingPlayer ? 'EDIT PLAYER' : 'ADD PLAYER'}</span>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && <div className="auth-error-banner"><span>⚠</span> {error}</div>}
                {editingPlayer && (
                  <div style={{ background:'var(--bg-elevated)', borderRadius:'var(--radius-md)', overflow:'hidden' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'14px', padding:'12px 14px' }}>
                      <div style={{ position:'relative', width:'56px', height:'56px', flexShrink:0 }}>
                        {editingPlayer.photo_url ? (
                          <img src={editingPlayer.photo_url} alt={editingPlayer.first_name} style={{ width:'56px', height:'56px', borderRadius:'50%', objectFit:'cover', border:'2px solid var(--border-light)' }} />
                        ) : (
                          <div style={{ width:'56px', height:'56px', borderRadius:'50%', background:'var(--bg-card)', border:'1px solid var(--border-light)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:'700', fontSize:'1.1rem', color:'var(--text-secondary)' }}>
                            {editingPlayer.first_name[0]}{editingPlayer.last_name[0]}
                          </div>
                        )}
                        <label htmlFor={`ep-${editingPlayer.id}`} style={{ position:'absolute', bottom:'-2px', right:'-2px', width:'20px', height:'20px', borderRadius:'50%', background: photoUploading ? 'var(--text-muted)' : 'var(--text-primary)', border:'2px solid var(--bg-elevated)', display:'flex', alignItems:'center', justifyContent:'center', cursor: photoUploading ? 'wait' : 'pointer', fontSize:'0.6rem', color:'var(--bg-primary)', fontWeight:'900' }} title={editingPlayer.photo_url ? 'Change photo' : 'Add photo'}>{photoUploading ? '…' : '+'}</label>
                        <input id={`ep-${editingPlayer.id}`} type="file" accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif" style={{ display:'none' }} disabled={photoUploading} onChange={e => { uploadPlayerPhoto(editingPlayer.id, e.target.files[0]); e.target.value = ''; }} />
                      </div>
                      <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
                        <span style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>
                          {photoUploading ? 'Uploading...' : editingPlayer.photo_url ? 'Click + to change photo' : 'Click + to add a photo'}
                        </span>
                        {editingPlayer.photo_url && !photoUploading && (
                          <button type="button" className="btn btn-ghost btn-sm"
                            style={{ fontSize:'0.75rem', color:'var(--error)', padding:'3px 8px', alignSelf:'flex-start' }}
                            onClick={() => removePlayerPhoto(editingPlayer.id)}>
                            ✕ Remove Photo
                          </button>
                        )}
                      </div>
                    </div>
                    {photoError && (
                      <div style={{ padding:'8px 14px 10px', background:'rgba(248,113,113,0.08)', borderTop:'1px solid rgba(248,113,113,0.2)', fontSize:'0.8rem', color:'#f87171', lineHeight:'1.4' }}>
                        {photoError}
                      </div>
                    )}
                  </div>
                )}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
                  <div className="form-group"><label className="form-label">First Name</label><input className="form-input" value={form.first_name} onChange={e => setForm(f=>({...f,first_name:e.target.value}))} required /></div>
                  <div className="form-group"><label className="form-label">Last Name</label><input className="form-input" value={form.last_name} onChange={e => setForm(f=>({...f,last_name:e.target.value}))} required /></div>
                </div>
                <div className="form-group"><label className="form-label">Date of Birth (optional)</label><input type="date" className="form-input" value={form.date_of_birth} onChange={e => setForm(f=>({...f,date_of_birth:e.target.value}))} /></div>
                <div className="form-group">
                  <label className="form-label">Positions (optional — select all that apply)</label>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'8px', marginTop:'6px' }}>
                    {POSITIONS.map(pos => (
                      <button key={pos} type="button" onClick={() => togglePosition(pos)}
                        style={{ padding:'6px 12px', borderRadius:'var(--radius-md)', border:'1px solid', fontSize:'0.8rem', fontWeight:'500', cursor:'pointer', transition:'all var(--transition)',
                          background: form.positions.includes(pos) ? 'var(--text-primary)' : 'var(--bg-elevated)',
                          color: form.positions.includes(pos) ? 'var(--bg-primary)' : 'var(--text-secondary)',
                          borderColor: form.positions.includes(pos) ? 'var(--text-primary)' : 'var(--border)',
                        }}>
                        {pos}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="form-group"><label className="form-label">Notes (optional)</label><textarea className="form-input" rows={2} value={form.notes} onChange={e => setForm(f=>({...f,notes:e.target.value}))} style={{resize:'vertical'}} /></div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline btn-sm" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>{saving ? 'Saving...' : editingPlayer ? 'Save Changes' : 'Add Player'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- My Profile ----
function MyProfile() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password:'', new_password:'', confirm_password:'' });
  const [pwError, setPwError] = useState('');
  const [pwSaved, setPwSaved] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);

  const fetchProfile = async () => {
    try { const r = await axios.get('/api/parent/profile'); setProfile(r.data.profile); setForm(r.data.profile); }
    catch (err) { console.error(err); }
  };

  useEffect(() => { fetchProfile(); }, []);

  const handleSave = async e => {
    e.preventDefault(); setSaving(true); setError(''); setSaved(false);
    try {
      await axios.patch('/api/parent/profile', { phone: form.phone, email_notifications: form.email_notifications, sms_notifications: form.sms_notifications });
      setSaved(true); setTimeout(() => setSaved(false), 2500);
      await fetchProfile();
    } catch (err) { setError(err.response?.data?.error || 'Failed to save.'); }
    finally { setSaving(false); }
  };

  const handlePasswordChange = async e => {
    e.preventDefault(); setPwError(''); setPwSaving(true);
    if (pwForm.new_password !== pwForm.confirm_password) { setPwError('Passwords do not match.'); setPwSaving(false); return; }
    try {
      await axios.post('/api/auth/change-password', { current_password: pwForm.current_password, new_password: pwForm.new_password });
      logout().finally(() => navigate('/login'));
    } catch (err) { setPwError(err.response?.data?.error || 'Failed to change password.'); }
    finally { setPwSaving(false); }
  };

  if (!form) return <div className="page"><p style={{ color:'var(--text-muted)' }}>Loading...</p></div>;

  return (
    <div className="page">
      <div className="page-header"><h1 className="page-title">MY PROFILE</h1><p className="page-subtitle">Manage your account and notification preferences</p></div>

      <form onSubmit={handleSave}>
        <div className="card" style={{ marginBottom:'20px' }}>
          <div className="stat-card-label" style={{ marginBottom:'16px' }}>Personal Information</div>
          <div style={{ display:'flex', flexDirection:'column', gap:'14px' }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
              <div className="form-group"><label className="form-label">First Name</label><input className="form-input" value={form.first_name||''} disabled style={{ opacity:0.5 }} /></div>
              <div className="form-group"><label className="form-label">Last Name</label><input className="form-input" value={form.last_name||''} disabled style={{ opacity:0.5 }} /></div>
            </div>
            <div className="form-group"><label className="form-label">Email</label><input className="form-input" value={form.email||''} disabled style={{ opacity:0.5 }} /></div>
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input className="form-input" value={form.phone||''} onChange={e => setForm(f=>({...f,phone:e.target.value}))} placeholder="+1 (555) 000-0000" />
            </div>
            <div style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>Member since {formatDate(form.created_at)}</div>
          </div>
        </div>

        <div className="card" style={{ marginBottom:'20px' }}>
          <div className="stat-card-label" style={{ marginBottom:'16px' }}>Notification Preferences</div>
          <div style={{ display:'flex', gap:'24px', flexWrap:'wrap', alignItems:'center' }}>
            <label style={{ display:'flex', alignItems:'center', gap:'10px', cursor:'pointer' }}>
              <input type="checkbox" checked={form.email_notifications ?? true} onChange={e => setForm(f=>({...f, email_notifications: e.target.checked}))} style={{ width:'16px', height:'16px', cursor:'pointer' }} />
              <span style={{ fontSize:'0.9rem', color:'var(--text-secondary)' }}>Email Notifications</span>
            </label>
            <label style={{ display:'flex', alignItems:'center', gap:'10px', cursor:'not-allowed', opacity:0.45 }}>
              <input type="checkbox" checked={false} disabled style={{ width:'16px', height:'16px', cursor:'not-allowed' }} />
              <span style={{ fontSize:'0.9rem', color:'var(--text-secondary)' }}>SMS Notifications</span>
            </label>
            <span style={{ fontSize:'0.78rem', color:'var(--text-muted)', fontStyle:'italic' }}>Option Returning Soon</span>
          </div>
        </div>

        {error && <div className="auth-error-banner" style={{ marginBottom:'16px' }}><span>⚠</span> {error}</div>}
        {saved && <div style={{ background:'rgba(74,222,128,0.1)', border:'1px solid rgba(74,222,128,0.3)', borderRadius:'var(--radius-md)', padding:'10px 16px', color:'#4ade80', fontSize:'0.875rem', marginBottom:'16px' }}>✓ Profile saved successfully</div>}
        <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save Profile'}</button>
      </form>

      <div className="card" style={{ marginTop:'24px' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div className="stat-card-label">Password</div>
          <button className="btn btn-outline btn-sm" onClick={() => setChangingPassword(c => !c)}>{changingPassword ? 'Cancel' : 'Change Password'}</button>
        </div>
        {pwSaved && <div style={{ background:'rgba(74,222,128,0.1)', border:'1px solid rgba(74,222,128,0.3)', borderRadius:'var(--radius-md)', padding:'10px 16px', color:'#4ade80', fontSize:'0.875rem', marginTop:'12px' }}>✓ Password changed successfully</div>}
        {changingPassword && (
          <form onSubmit={handlePasswordChange} style={{ marginTop:'16px', display:'flex', flexDirection:'column', gap:'14px' }}>
            {pwError && <div className="auth-error-banner"><span>⚠</span> {pwError}</div>}
            <div className="form-group"><label className="form-label">Current Password</label><input type="password" className="form-input" value={pwForm.current_password} onChange={e => setPwForm(f=>({...f,current_password:e.target.value}))} required /></div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
              <div className="form-group"><label className="form-label">New Password</label><input type="password" className="form-input" value={pwForm.new_password} onChange={e => setPwForm(f=>({...f,new_password:e.target.value}))} required minLength={8} /></div>
              <div className="form-group"><label className="form-label">Confirm Password</label><input type="password" className="form-input" value={pwForm.confirm_password} onChange={e => setPwForm(f=>({...f,confirm_password:e.target.value}))} required /></div>
            </div>
            <div><button type="submit" className="btn btn-primary btn-sm" disabled={pwSaving}>{pwSaving ? 'Saving...' : 'Update Password'}</button></div>
          </form>
        )}
      </div>
    </div>
  );
}

// ---- Support ----
const TICKET_STATUS = {
  open:        { bg:'rgba(251,191,36,0.15)', color:'#fbbf24', label:'Open' },
  in_progress: { bg:'rgba(96,165,250,0.15)', color:'#60a5fa', label:'In Progress' },
  resolved:    { bg:'rgba(74,222,128,0.15)', color:'#4ade80', label:'Resolved' },
};
const fmtDateTime = ts => { if (!ts) return '—'; const d=new Date(ts); return d.toLocaleDateString('en-US',{month:'short',day:'numeric'})+' '+d.toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'}); };

function SupportTicketModal({ ticket, onClose, onUpdated }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [currentTicket, setCurrentTicket] = useState(ticket);
  const [loadingMsgs, setLoadingMsgs] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  const fetchDetail = useCallback(async () => {
    try {
      const r = await axios.get(`/api/support/tickets/${ticket.id}`);
      setMessages(r.data.messages);
      setCurrentTicket(r.data.ticket);
      await axios.patch('/api/support/notifications/read', { ticket_id: ticket.id });
    } catch (err) { console.error(err); }
    finally { setLoadingMsgs(false); }
  }, [ticket.id]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const handleSend = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await axios.post(`/api/support/tickets/${ticket.id}/messages`, { message: reply });
      setReply('');
      await fetchDetail();
      if (onUpdated) onUpdated();
    } catch (err) { console.error(err); }
    finally { setSending(false); }
  };

  const st = TICKET_STATUS[currentTicket.status] || { bg:'var(--bg-elevated)', color:'var(--text-muted)', label: currentTicket.status };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:'580px', display:'flex', flexDirection:'column', maxHeight:'85vh' }}>
        <div className="modal-header" style={{ gap:'12px' }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div className="modal-title" style={{ fontSize:'1.05rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginBottom:'6px' }}>{currentTicket.subject}</div>
            <span style={{ background:st.bg, color:st.color, padding:'3px 10px', borderRadius:'20px', fontSize:'0.75rem', fontWeight:'600' }}>{st.label}</span>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:'16px 24px', display:'flex', flexDirection:'column', gap:'12px' }}>
          {loadingMsgs ? <p style={{ color:'var(--text-muted)', fontSize:'0.875rem' }}>Loading...</p> : messages.map(msg => {
            const isAdmin = msg.role === 'admin';
            return (
              <div key={msg.id} style={{ display:'flex', flexDirection:'column', alignItems:isAdmin ? 'flex-start' : 'flex-end' }}>
                <div style={{
                  maxWidth:'80%',
                  background: isAdmin ? 'rgba(96,165,250,0.1)' : 'var(--bg-elevated)',
                  border:`1px solid ${isAdmin ? 'rgba(96,165,250,0.25)' : 'var(--border)'}`,
                  borderRadius: isAdmin ? '12px 12px 12px 4px' : '12px 12px 4px 12px',
                  padding:'10px 14px',
                }}>
                  <div style={{ fontSize:'0.78rem', fontWeight:'600', color: isAdmin ? '#60a5fa' : 'var(--text-primary)', marginBottom:'4px' }}>
                    {isAdmin ? 'Support Team' : 'You'}
                  </div>
                  <div style={{ fontSize:'0.875rem', color:'var(--text-secondary)', lineHeight:'1.5', whiteSpace:'pre-wrap', wordBreak:'break-word' }}>{msg.message}</div>
                  <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:'6px', textAlign: isAdmin ? 'left' : 'right' }}>{fmtDateTime(msg.created_at)}</div>
                </div>
              </div>
            );
          })}
        </div>
        {currentTicket.status !== 'resolved' ? (
          <div style={{ padding:'12px 24px 16px', borderTop:'1px solid var(--border)', display:'flex', flexDirection:'column', gap:'8px' }}>
            <textarea className="form-input" rows={3} placeholder="Write a reply..." value={reply} onChange={e => setReply(e.target.value)} style={{ resize:'vertical', fontSize:'0.875rem' }}
              onKeyDown={e => { if (e.key==='Enter' && (e.metaKey||e.ctrlKey)) handleSend(); }} />
            <div style={{ display:'flex', justifyContent:'flex-end' }}>
              <button className="btn btn-sm" onClick={handleSend} disabled={sending || !reply.trim()} style={{ opacity:(!reply.trim()||sending) ? 0.5 : 1 }}>
                {sending ? 'Sending...' : 'Send Reply'}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ padding:'12px 24px 16px', borderTop:'1px solid var(--border)', textAlign:'center', color:'var(--text-muted)', fontSize:'0.85rem' }}>
            This ticket has been resolved. Open a new ticket if you need further help.
          </div>
        )}
      </div>
    </div>
  );
}

function ParentSupport({ onNotifRead }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ subject:'', message:'' });
  const [submitting, setSubmitting] = useState(false);
  const [newError, setNewError] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);

  const fetchTickets = useCallback(async () => {
    try {
      const r = await axios.get('/api/support/tickets/my');
      setTickets(r.data.tickets);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchTickets();
    axios.patch('/api/support/notifications/read').catch(() => {});
    if (onNotifRead) onNotifRead();
  }, [fetchTickets, onNotifRead]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!newForm.subject.trim() || !newForm.message.trim()) { setNewError('Subject and message are required.'); return; }
    setSubmitting(true); setNewError('');
    try {
      await axios.post('/api/support/tickets', newForm);
      setNewForm({ subject:'', message:'' });
      setShowNew(false);
      await fetchTickets();
    } catch (err) { setNewError(err.response?.data?.error || 'Failed to submit ticket.'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="page">
      <div className="page-header" style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:'16px' }}>
        <div>
          <h1 className="page-title">SUPPORT</h1>
          <p className="page-subtitle">Get help from our team. We'll respond as soon as possible.</p>
        </div>
        {!showNew && (
          <button className="btn btn-primary" onClick={() => setShowNew(true)}>+ New Ticket</button>
        )}
      </div>

      {showNew && (
        <div className="card" style={{ marginBottom:'24px', padding:'24px', background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)' }}>
          <div style={{ fontWeight:'600', fontSize:'1rem', color:'var(--text-primary)', marginBottom:'16px' }}>Open a Support Ticket</div>
          <form onSubmit={handleSubmit} style={{ display:'flex', flexDirection:'column', gap:'14px' }}>
            <div className="form-group">
              <label className="form-label">Subject</label>
              <input className="form-input" value={newForm.subject} onChange={e => setNewForm(f=>({...f,subject:e.target.value}))} placeholder="Brief description of your issue" maxLength={255} />
            </div>
            <div className="form-group">
              <label className="form-label">Message</label>
              <textarea className="form-input" rows={4} value={newForm.message} onChange={e => setNewForm(f=>({...f,message:e.target.value}))} placeholder="Please describe your issue in detail..." style={{ resize:'vertical' }} />
            </div>
            {newError && <div className="auth-error-banner"><span>⚠</span> {newError}</div>}
            <div style={{ display:'flex', gap:'10px' }}>
              <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>{submitting ? 'Submitting...' : 'Submit Ticket'}</button>
              <button type="button" className="btn btn-outline btn-sm" onClick={() => { setShowNew(false); setNewError(''); }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {loading ? <p style={{ color:'var(--text-muted)' }}>Loading tickets...</p> : tickets.length === 0 && !showNew ? (
        <div className="empty-state">
          <div className="empty-state-icon">🎫</div>
          <p className="empty-state-text">No support tickets yet</p>
          <button className="btn btn-primary btn-sm" style={{ marginTop:'8px' }} onClick={() => setShowNew(true)}>Open a Ticket</button>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
          {tickets.map(t => {
            const st = TICKET_STATUS[t.status] || { bg:'var(--bg-elevated)', color:'var(--text-muted)', label:t.status };
            return (
              <div key={t.id} onClick={() => setSelectedTicket(t)} style={{
                background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)',
                padding:'16px 20px', cursor:'pointer', transition:'border-color 0.15s, background 0.15s',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor='var(--border-light)'; e.currentTarget.style.background='var(--bg-elevated)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.background='var(--bg-card)'; }}>
                <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'12px' }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:'600', color:'var(--text-primary)', fontSize:'0.95rem', marginBottom:'4px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.subject}</div>
                    <div style={{ fontSize:'0.78rem', color:'var(--text-muted)' }}>
                      #{t.id} · {t.message_count} {parseInt(t.message_count)===1?'message':'messages'} · Last activity {fmtDateTime(t.last_message_at || t.updated_at)}
                    </div>
                  </div>
                  <span style={{ background:st.bg, color:st.color, padding:'3px 10px', borderRadius:'20px', fontSize:'0.72rem', fontWeight:'600', whiteSpace:'nowrap', flexShrink:0 }}>{st.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedTicket && (
        <SupportTicketModal ticket={selectedTicket} onClose={() => setSelectedTicket(null)} onUpdated={() => { setSelectedTicket(null); fetchTickets(); if (onNotifRead) onNotifRead(); }} />
      )}
    </div>
  );
}

export default function ParentDashboard() {
  const [supportBadge, setSupportBadge] = useState(0);
  const [sharedInstructors, setSharedInstructors] = useState([]);
  const [sharedLoading, setSharedLoading] = useState(true);
  const [sharedError, setSharedError] = useState('');

  const fetchSharedCalendars = useCallback(() => {
    setSharedLoading(true);
    setSharedError('');
    axios.get('/api/parent/shared-calendars')
      .then(r => setSharedInstructors(r.data.instructors || []))
      .catch(() => { setSharedInstructors([]); setSharedError('Unable to load shared calendars.'); })
      .finally(() => setSharedLoading(false));
  }, []);

  useEffect(() => { fetchSharedCalendars(); }, [fetchSharedCalendars]);

  const fetchSupportBadge = useCallback(() => {
    axios.get('/api/support/notifications').then(r => setSupportBadge(r.data.count)).catch(() => {});
  }, []);

  useEffect(() => { fetchSupportBadge(); }, [fetchSupportBadge]);

  const navItems = useMemo(() => {
    const items = BASE_NAV.map(item =>
      item.path === '/dashboard/support' ? { ...item, badge: supportBadge } : item
    );
    if (sharedInstructors.length > 0 || sharedError) {
      items.splice(2, 0, { path: '/dashboard/shared-calendar', label: 'Shared Calendar', icon: '📅' });
    }
    return items;
  }, [supportBadge, sharedInstructors, sharedError]);

  return (
    <Layout navItems={navItems} helpUrl="/help/parent">
      <Routes>
        <Route index element={<ParentHome supportBadge={supportBadge} />} />
        <Route path="sessions" element={<MySessions />} />
        <Route path="shared-calendar" element={<SharedCalendars instructors={sharedInstructors} loading={sharedLoading} error={sharedError} onRetry={fetchSharedCalendars} />} />
        <Route path="book" element={<BookSession />} />
        <Route path="players" element={<MyPlayers />} />
        <Route path="profile" element={<MyProfile />} />
        <Route path="support" element={<ParentSupport onNotifRead={fetchSupportBadge} />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  );
}
